 
import { DeleteOutlined } from "@ant-design/icons";
import { message, Spin, Upload } from "antd";
import type { ReactNode } from "react";
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { cn } from "sipli-fleet-core/utils/cn";
import UploadIcon from "../../assets/icons/upload.svg";


type UploadedFileItem = {
  file: File;
  documentType: "CSV" | "PDF" | "Image";
  url: string;
  uid?: string;
};

type MultiImageUploaderProps = {
  setFile?: (files: UploadedFileItem[]) => void;
  isShowMultiListing?: boolean;
  isAllowSingleFile?: boolean;
  parentHandleBeforeUpload?: (file: File) => void;
  renderCustomUi?: ReactNode;
  uploadClassName?: string;
  renderOutsideUploadUi?: ReactNode;
  isCsvFormat?: boolean;
};

export type MultiImageUploaderRef = {
  clickInput: () => void;
  handleRemove: (index: number) => void;
};

const MultiImageUploader = forwardRef<
  MultiImageUploaderRef,
  MultiImageUploaderProps
>(
  (
    {
      setFile = () => {},
      isShowMultiListing = true,
      isAllowSingleFile = false,
      parentHandleBeforeUpload = () => {},
      renderCustomUi = null,
      uploadClassName = "",
      renderOutsideUploadUi = null,
      isCsvFormat = false,
    },
    ref,
  ) => {
    const [fileList, setFileList] = useState<UploadedFileItem[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      setFile(fileList);
    }, [fileList, setFile]);

    const handleBeforeFileUpload = async (file: File) => {
      if (isCsvFormat) {
        setLoading(true);

        try {
          if (!file.type.includes("csv")) {
            message.error("You can only upload CSV files");
            throw new Error("Invalid file type");
          }

          const isLt25M = file.size / 1024 / 1024 < 25;
          if (!isLt25M) {
            message.error("File must be smaller than 25MB");
            throw new Error("File size exceeds 25MB");
          }

          const fileContent = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsText(file);
            reader.onload = () => resolve(String(reader.result));
            reader.onerror = (error) => reject(error);
          });

          const rowCount = fileContent.split("\n").length;
          if (rowCount > 50) {
            message.error("File must not contain more than 50 rows");
            throw new Error("Exceeded maximum allowed entries");
          }

          parentHandleBeforeUpload(file);

          const statePayload: UploadedFileItem = {
            file,
            documentType: "CSV",
            url: URL.createObjectURL(file),
          };

          setFileList([...fileList, statePayload]);
        } catch (error) {
          console.error("Error:", (error as Error).message);
        } finally {
          setLoading(false);
        }

        return Promise.reject(false);
      } else {
        setLoading(true);

        try {
          if (!file.type.includes("image") && !file.type.includes("pdf")) {
            message.error("You can only upload image or pdf files");
            throw new Error("Invalid file type");
          }

          const isLt5M = file.size / 1024 / 1024 < 5;
          if (!isLt5M) {
            message.error("Image must be smaller than 5MB");
            throw new Error("File size exceeds 5MB");
          }

          parentHandleBeforeUpload(file);

          let base64Img = "";
          if (file.type.includes("image")) {
            base64Img = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onload = () => resolve(String(reader.result));
            });
          }

          const statePayload: UploadedFileItem = {
            file,
            documentType: file.type.includes("pdf") ? "PDF" : "Image",
            url: base64Img || URL.createObjectURL(file),
          };

          setFileList([...fileList, statePayload]);
        } catch (error) {
          console.error("Error:", (error as Error).message);
        } finally {
          setLoading(false);
        }

        return Promise.reject(false);
      }
    };

    const handleRemove = (index: number) => {
      const fileTemp = [...fileList];
      fileTemp.splice(index, 1);
      setFileList(fileTemp);
    };

    const fileInputRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(ref, () => ({
      clickInput: () => {
        const findInputFile =
          fileInputRef?.current?.querySelector<HTMLInputElement>(
            'input[type="file"]',
          );

        if (findInputFile) {
          findInputFile.disabled = false;
          findInputFile?.click();
        }
      },
      handleRemove,
    }));

    return (
      <Spin spinning={loading}>
        <div ref={fileInputRef}>
          <Upload
            accept={isCsvFormat ? ".csv" : ".jpg, .jpeg, .png, .pdf"}
            name="file"
            fileList={fileList as any}
            listType="picture-card"
            showUploadList={false}
            beforeUpload={handleBeforeFileUpload}
            data-testid="image-upload"
            className={cn(
              "custom-multi-imageuploader-container relative",
              uploadClassName,
            )}
            disabled={isAllowSingleFile && fileList?.length > 0}
          >
            {fileList?.length > 0 ? (
              <div className="flex flex-col">
                {fileList?.map((singleFile, index) => {
                  return (
                    <div className="mb-2" key={index}>
                      {singleFile.documentType === "Image" ? (
                        <img
                          src={singleFile.url}
                          alt="Uploaded"
                          className="w-full h-full object-cover max-w-[50px]"
                        />
                      ) : (
                        singleFile?.file?.name
                      )}

                      <DeleteOutlined
                        className="absolute right-[10px] top-[10px]"
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleRemove(index);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            ) : renderCustomUi ? (
              renderCustomUi
            ) : (
              <div className="flex flex-col items-center justify-center">
                <div className="mb-3">
                  <UploadIcon />
                </div>

                <p>
                  <span className="text-blue">Browse</span> to choose a file
                </p>
              </div>
            )}
          </Upload>

          {renderOutsideUploadUi}

          {fileList.length > 0 && isShowMultiListing && (
            <div className="image-upload-items-container">
              {fileList.map(({ uid, documentType }) => (
                <div className="image-upload-sub-items-container" key={uid}>
                  <p>{documentType}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </Spin>
    );
  },
);

MultiImageUploader.displayName = "MultiImageUploader";

export default memo(MultiImageUploader);
